import React, { useState } from 'react';
import Seo from '../components/Seo';
import { Flame, ArrowRight, Mail, Send } from 'lucide-react';
import { BRAND } from '../mock/mock';
import Reveal from '../components/Reveal';
import { useToast } from '../hooks/use-toast';
import { submitContact } from '../lib/apiClient';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'A few details missing', description: 'Please add your name, email and a short message.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast({ title: 'Message sent', description: 'Thank you for reaching out! We’ll be in touch soon.' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-fire-ink">
      <Seo title="Contact" path="/contact" description="Reach out to the After the Fire™ team for booking inquiries, media requests, group orders, or simply to connect with Dr. Gee." />
      {/* Hero banner */}
      <section className="pt-24">
        <div className="relative">
          <img src="/brand/contact-banner.jpg" alt="Contact Dr. Gee" className="w-full h-[360px] md:h-[480px] object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-fire-ink via-fire-ink/60 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1400px] mx-auto px-5 lg:px-8 w-full">
              <div className="max-w-lg">
                <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">AFTER THE FIRE</span>
                <h1 className="font-script text-6xl md:text-7xl text-fire-goldlight mt-2 leading-none">Contact Us</h1>
                <p className="text-fire-cream/80 font-body mt-4 leading-relaxed">
                  We’d love to hear from you! Whether you have a question, need more information, or want to book Dr. Gee for your next event — we’re here for you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-24 ember-texture">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_1.3fr] gap-12 items-start">
          {/* Left info */}
          <Reveal>
            <Flame className="w-9 h-9 text-fire-gold mb-5 animate-flicker" strokeWidth={1.4} />
            <h2 className="font-display text-3xl text-fire-cream mb-4">Let’s Talk</h2>
            <p className="text-fire-cream/70 font-body leading-relaxed mb-8">
              Reach out for booking inquiries, media requests, group merchandise orders, or simply to connect with the After the Fire™ team.
            </p>
            <a href={`mailto:${BRAND.email}`} className="card-dark rounded-2xl p-6 flex items-center gap-4 hover-lift mb-4">
              <div className="w-12 h-12 rounded-full bg-fire-maroon/30 border border-fire-gold/30 flex items-center justify-center">
                <Mail className="w-5 h-5 text-fire-goldlight" />
              </div>
              <div>
                <p className="font-marcellus tracking-widest text-fire-gold text-[11px]">EMAIL US</p>
                <p className="font-body text-fire-cream">{BRAND.email}</p>
              </div>
            </a>
            <div className="card-dark rounded-2xl p-6">
              <p className="font-cormorant italic text-fire-goldlight text-xl leading-relaxed">
                “There’s an exit. Come this way.”
              </p>
              <p className="font-marcellus tracking-[0.25em] text-fire-cream/50 text-[11px] mt-4">TESTED BY THE FIRE. REFINED BY THE FLAME.™</p>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={120}>
            <form onSubmit={submit} className="card-dark rounded-2xl p-7 md:p-9 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <CField label="Your Name *" value={form.name} onChange={(v) => update('name', v)} placeholder="Jane Doe" />
                <CField label="Email *" type="email" value={form.email} onChange={(v) => update('email', v)} placeholder="you@example.com" />
              </div>
              <CField label="Subject" value={form.subject} onChange={(v) => update('subject', v)} placeholder="How can we help?" />
              <div>
                <label className="block font-body text-sm text-fire-cream/70 mb-2">Message *</label>
                <textarea value={form.message} onChange={(e) => update('message', e.target.value)} rows={6} placeholder="Tell us what’s on your heart…" className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors resize-none" />
              </div>
              <button type="submit" disabled={submitting} className="btn-gold w-full rounded-full py-4 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
                {submitting ? 'Sending…' : (<>Send Message <Send className="w-4 h-4" /></>)}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const CField = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="block font-body text-sm text-fire-cream/70 mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors" />
  </div>
);

export default Contact;
